import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Users } from "lucide-react";
import api from "@/lib/api";
import { useCities } from "@/context/CityContext";
import Header from "@/components/Header";
import ProductCard from "@/components/ProductCard";
import ListToolbar from "@/components/ListToolbar";

export default function Marketplace() {
  const nav = useNavigate();
  const { cities, visitor } = useCities();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ size: "", surface: "", price: "" });
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("newest");
  const [options, setOptions] = useState({ sizes: [], surfaces: [] });
  const [visitors, setVisitors] = useState(null);
  const [buyers, setBuyers] = useState(null);
  const [cats, setCats] = useState([]);

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);

  useEffect(() => { api.get("/stats/stock-sold").then(({ data }) => { setVisitors(data.visitors); setBuyers(data.buyers); }).catch(() => {}); }, []);

  useEffect(() => {
    if (!visitor) { nav("/welcome", { replace: true }); return; }
    if (!cities.length) { nav("/select-city", { replace: true }); }
  }, [visitor, cities, nav]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (cities.length) params.set("cities", cities.join(","));
    api.get(`/marketplace/options?${params.toString()}`).then(({ data }) => setOptions(data));
  }, [cities]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (cities.length) params.set("cities", cities.join(","));
    if (category) params.set("category", category);
    if (search) params.set("q", search);
    if (filters.size) params.set("size", filters.size);
    if (filters.surface) params.set("surface", filters.surface);
    if (filters.price === "low") params.set("max_price", "500");
    if (filters.price === "mid") { params.set("min_price", "500"); params.set("max_price", "800"); }
    if (filters.price === "high") params.set("min_price", "800");
    params.set("sort", sort);
    const { data } = await api.get(`/marketplace/products?${params.toString()}`);
    setProducts(data);
    setLoading(false);
  }, [cities, search, filters, sort, category]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="min-h-screen bg-background">
      <Header search={search} setSearch={setSearch} onSearch={setSearch} />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-5">
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">
            Clearance Stock in {cities.join(" & ") || "your city"}
          </h1>
          <p className="text-muted-foreground text-sm font-medium">Actual stock, ready to clear. Contact sellers directly on WhatsApp.</p>
          {visitors !== null && visitors > 0 && (
            <div data-testid="marketplace-visitor-count" className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/30 px-4 py-1.5">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-sm font-bold text-primary">{visitors.toLocaleString("en-IN")}+</span>
              <span className="text-sm font-medium text-muted-foreground">people visited the marketplace till date</span>
            </div>
          )}
          {buyers !== null && buyers > 0 && (
            <div data-testid="marketplace-buyer-count" className="mt-3 ml-0 sm:ml-2 inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-4 py-1.5">
              <Users className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-bold text-emerald-600">{buyers.toLocaleString("en-IN")}+</span>
              <span className="text-sm font-medium text-muted-foreground">buyers joined the marketplace</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-4" data-testid="category-tabs">
          {[{ value: "", label: "All" }, ...cats].map((c) => (
            <button
              key={c.value || "all"}
              data-testid={`cat-tab-${c.value || "all"}`}
              onClick={() => setCategory(c.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-bold border transition-colors ${category === c.value ? "bg-primary text-white border-primary" : "bg-card text-muted-foreground border-border hover:text-foreground"}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <ListToolbar
          placeholder="Search products, size, surface…"
          search={search}
          setSearch={setSearch}
          filters={[
            { key: "size", label: "Size", value: filters.size, options: options.sizes.map((s) => ({ value: s, label: s })) },
            { key: "surface", label: "Surface", value: filters.surface, options: options.surfaces.map((s) => ({ value: s, label: s })) },
            { key: "price", label: "Price", value: filters.price, options: [
              { value: "low", label: "Under ₹500" }, { value: "mid", label: "₹500 – ₹800" }, { value: "high", label: "Above ₹800" },
            ] },
          ]}
          onFilter={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
          sort={sort}
          setSort={setSort}
          sorts={[
            { value: "newest", label: "Newest" },
            { value: "price_low", label: "Price: Low → High" },
            { value: "price_high", label: "Price: High → Low" },
            { value: "most_viewed", label: "Most Viewed" },
          ]}
          onReset={() => { setSearch(""); setFilters({ size: "", surface: "", price: "" }); setSort("newest"); }}
        />

        {loading ? (
          <div className="text-center text-muted-foreground py-24">Loading stock…</div>
        ) : products.length === 0 ? (
          <div className="text-center py-24 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-semibold">No stock found for these filters.</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {products.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
